import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Brain, User, Mail, Phone, Shield, Lock } from "lucide-react";
import { usePageTracking } from "../hooks/useAnalytics";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function RegisterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  
  // Track registration page view
  usePageTracking(id);

  // Offer calculations
  const hasOffer = training?.offer_enabled && training?.offer_percentage && training?.offer_end_date;
  const offerEndDate = hasOffer ? new Date(training.offer_end_date) : null;
  const isOfferValid = hasOffer && offerEndDate && offerEndDate >= new Date();
  const offerPrice = isOfferValid ? Math.round(training.price * (1 - training.offer_percentage / 100)) : training?.price;
  const displayPrice = isOfferValid ? offerPrice : training?.price;

  useEffect(() => {
    axios.get(`${API}/trainings/${id}`)
      .then(r => setTraining(r.data))
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Please fill in all fields");
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/participants/register`, {
        training_id: id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        origin_url: window.location.origin  // Send the current domain for payment return
      });

      const { payment_session_id, order_id } = res.data;

      // Use Cashfree JS SDK for payment
      if (!window.Cashfree) {
        setError("Payment gateway not loaded. Please refresh and try again.");
        setSubmitting(false);
        return;
      }

      const cashfree = window.Cashfree({ mode: "production" });
      
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        returnUrl: `${window.location.origin}/payment-success?order_id=${order_id}`,
        redirectTarget: "_self"
      }).then(result => {
        if (result.error) {
          setError(result.error.message || "Payment failed. Please try again.");
          setSubmitting(false);
        }
      }).catch(err => {
        setError("Payment initialization failed. Please try again.");
        setSubmitting(false);
      });

    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="marketing-bg min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="marketing-bg min-h-screen">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <button
            data-testid="back-to-training-btn"
            onClick={() => navigate(`/trainings/${id}`)}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={18} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF]">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <h1 className="font-outfit font-black text-4xl text-white mb-2">Complete Registration</h1>
            <p className="text-[#A1A1AA] mb-8 font-dm-sans">Fill in your details to reserve your seat.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2 font-dm-sans">Full Name *</label>
                <div className="relative">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    data-testid="name-input"
                    type="text"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Enter your full name"
                    className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-[#00F0FF] text-white pl-11 pr-4 py-3.5 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2 font-dm-sans">Email Address *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    data-testid="email-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="Enter your email address"
                    className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-[#00F0FF] text-white pl-11 pr-4 py-3.5 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2 font-dm-sans">Phone Number *</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    data-testid="phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="10-digit mobile number"
                    className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-[#00F0FF] text-white pl-11 pr-4 py-3.5 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                    required
                  />
                </div>
              </div>

              {error && (
                <div data-testid="register-error" className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 font-dm-sans">
                  {error}
                </div>
              )}

              <button
                data-testid="pay-btn"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#00F0FF] text-black font-outfit font-black text-lg py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Pay &#8377;{displayPrice?.toLocaleString('en-IN')} & Register
                  </>
                )}
              </button>

              <div className="flex items-center gap-2 justify-center">
                <Shield size={14} className="text-zinc-500" />
                <p className="text-zinc-500 text-xs font-dm-sans">Secured by Cashfree Payment Gateway</p>
              </div>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-zinc-900/80 border border-zinc-800 p-6 sticky top-24">
              <h3 className="font-outfit font-bold text-white text-xl mb-5">Order Summary</h3>
              {training && (
                <>
                  <img
                    src={training.image_url || "https://images.unsplash.com/photo-1646583288948-24548aedffd8?w=400&h=200&fit=crop"}
                    alt={training.title}
                    className="w-full h-32 object-cover mb-5 border border-zinc-800"
                  />
                  <h4 className="font-outfit font-bold text-white text-lg mb-4 leading-tight">{training.title}</h4>
                  <div className="space-y-2 pb-4 border-b border-zinc-800 mb-4 text-sm font-dm-sans">
                    <div className="flex justify-between text-[#A1A1AA]">
                      <span>Date</span><span className="text-white">{training.date}</span>
                    </div>
                    <div className="flex justify-between text-[#A1A1AA]">
                      <span>Time</span><span className="text-white">{training.time}</span>
                    </div>
                    <div className="flex justify-between text-[#A1A1AA]">
                      <span>Duration</span><span className="text-white">{training.duration}</span>
                    </div>
                    <div className="flex justify-between text-[#A1A1AA]">
                      <span>Instructor</span><span className="text-white">{training.instructor}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#A1A1AA] font-dm-sans text-sm">Total Amount</span>
                    <div className="text-right">
                      {isOfferValid ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="font-outfit font-black text-2xl text-[#00F0FF]">&#8377;{offerPrice.toLocaleString('en-IN')}</span>
                            <span className="font-outfit text-sm text-zinc-500 line-through">&#8377;{training.price.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex items-center justify-end gap-2 mt-1">
                            <span className="text-xs font-bold px-2 py-0.5 bg-red-500 text-white">{training.offer_percentage}% OFF</span>
                            <span className="text-xs text-zinc-400 font-dm-sans">
                              Ends {offerEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="font-outfit font-black text-2xl text-[#00F0FF]">&#8377;{training.price.toLocaleString('en-IN')}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
